import { useRef, useState, useEffect, useCallback } from "react";
import CreativeReportDateSelector from "../../../creatives/components/CreativeReportDateSelector";
import { CreativeReportData, Platform } from "@/screens/creatives/types";
import { getDataConfigurations } from "@/screens/creatives/apiService";
import { useGlobalStore } from "@/GlobalStore";
import ReportTitle from "./ReportTitle";
import { CreativeSectionConfiguration, CreativeReportMetadata } from "@/types";
import { Separator } from "@/components/ui/separator";
import CommentAnalysisTable from "./CommentAnalysisTable";
import { fetchAdList } from "../../apiService";
import FacebookIcon from "@/assets/icons/facebook.svg";
import InstagramIcon from "@/assets/icons/instagram.svg";
import TikTokIcon from "@/assets/icons/tiktok.png";
import { RxOpenInNewWindow } from "react-icons/rx";
import { CancelTokenSource } from "axios";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useCommentAnalysisStore } from "../../store";
import NoCommentsMessage from "./NoCommentsMessage";
import AdCard from "@/components/common/adCard";
import { getPlatformIcon } from "@/screens/utils/Helpers";
import DownloadAction from "@/components/common/adCard/actions/DownloadAction";
import PageLoader from "@/components/common/loaders/PageLoader";
import { SqButton } from "@/components/squircle-ui/button";
import { IconFilter } from "@tabler/icons-react";
import Filters from "@/components/common/filters/Filters";
import CreativeReportDropdowns from "@/screens/creatives/components/CreativeReportDropdowns";
import LinkedInIcon from "@/assets/icons/linkedin-logo.png";
import ExpiredTokenGuard from "@/router/guards/ExpiredTokenGuard";

const PLATFORM_CONFIG = {
  facebook: {
    icon: FacebookIcon,
    name: "Facebook",
  },
  instagram: {
    icon: InstagramIcon,
    name: "Instagram",
  },
  tiktok: {
    icon: TikTokIcon,
    name: "TikTok",
  },
  linkedin: {
    icon: LinkedInIcon,
    name: "LinkedIn",
  },
} as const;

const CommentAnalysisReport = () => {
  const {
    selectedAccount,
    sidebarCollapsed,
    allowedDateRange,
    isAccountSynced,
    dataConfigurations,
    setDataConfigurations,
  } = useGlobalStore();
  const { selectedGroup, selectedSidebarItem } = useCommentAnalysisStore();
  const [selectedDate, setSelectedDate] = useState<CreativeReportMetadata | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Add refs for component lifecycle and account tracking
  const isMounted = useRef(true);
  const adAccountRef = useRef<string>();
  const adSelectedDateRef = useRef<CreativeReportMetadata | null>(null);
  const adSelectedFiltersRef = useRef<CreativeSectionConfiguration | null>(null);

  // Add filter states
  const [selectedFilters, setSelectedFilters] =
    useState<CreativeSectionConfiguration | null>({ dropdowns: { creative_type: null } });

  // Add state for loading data configurations
  const [isLoadingConfigurations, setIsLoadingConfigurations] = useState(false);

  // Add new state for ad list
  const [adList, setAdList] = useState<CreativeReportData | null>(null);
  const [isLoadingAdList, setIsLoadingAdList] = useState(false);

  // Add new ref for ad list polling
  const adListTimeoutRef = useRef<NodeJS.Timeout>();

  // Add new cancel token ref
  const adListCancelTokenRef = useRef<CancelTokenSource>();

  // Add new state for pagination
  const [visibleAds, setVisibleAds] = useState<number>(3); // Initial number of ads to show

  // Add intersection observer ref
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Add new state for ad list error
  const [adListError, setAdListError] = useState<string | null>(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (adListTimeoutRef.current) {
        clearTimeout(adListTimeoutRef.current);
      }
    };
  }, []);

  const fetchDataConfigurations = useCallback(async () => {
    // Get the current accountId at the start of the function
    const currentAccountId = selectedAccount?.id;

    if (!currentAccountId || !isMounted.current) return;

    setIsLoadingConfigurations(true);
    try {
      const response = await getDataConfigurations(currentAccountId);
      // Check if the component is still mounted and account hasn't changed
      if (!isMounted.current) return;

      if (response) {
        // Update the global store with the configurations
        setDataConfigurations(response.configuration);
      }
    } catch (error) {
      console.error("Error fetching data configurations:", error);
    } finally {
      setIsLoadingConfigurations(false);
    }
  }, [selectedAccount?.id, setDataConfigurations]);

  useEffect(() => {
    // Reset all states when account changes
    setAdList(null);
    setSelectedDate(null);
    setSelectedFilters({ dropdowns: { creative_type: null } });
    setIsLoadingConfigurations(false);
    setIsLoadingAdList(false);

    // Fetch data configurations
    void fetchDataConfigurations();
  }, [selectedAccount?.id, fetchDataConfigurations]);

  // Add new effect to fetch ad list when selectedGroup is assets
  useEffect(() => {
    const fetchAds = async () => {
      // Clear any existing timeout and cancel ongoing request
      if (adListTimeoutRef.current) {
        clearTimeout(adListTimeoutRef.current);
      }
      if (adListCancelTokenRef.current) {
        adListCancelTokenRef.current.cancel("New request initiated");
      }

      if (
        !selectedAccount?.id ||
        !isAccountSynced ||
        !selectedDate ||
        selectedGroup.key !== "assets" ||
        !isMounted.current
      )
        return;

      setIsLoadingAdList(true);
      setAdListError(null); // Reset error state before new fetch

      // Create new cancel token
      adListCancelTokenRef.current = axios.CancelToken.source();

      try {
        const response = await fetchAdList({
          accountId: selectedAccount.id,
          reportData: selectedDate,
          sectionData: selectedFilters || undefined,
          cancelToken: adListCancelTokenRef.current.token,
        });

        if (!isMounted.current) return;

        setAdList(response);
        adAccountRef.current = selectedAccount.id;
        adSelectedDateRef.current = selectedDate;
        adSelectedFiltersRef.current = selectedFilters;

        if (response?.status === "pending") {
          adListTimeoutRef.current = setTimeout(() => void fetchAds(), 5000);
        } else {
          setIsLoadingAdList(false);
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching ad list:", error);
          setAdListError(
            error instanceof Error ? error.message : "An unexpected error occurred",
          );
          setIsLoadingAdList(false);
        }
      }
    };

    void fetchAds();

    return () => {
      if (adListTimeoutRef.current) {
        clearTimeout(adListTimeoutRef.current);
      }
      if (adListCancelTokenRef.current) {
        adListCancelTokenRef.current.cancel("Component unmounted");
      }
    };
  }, [
    selectedAccount?.id,
    isAccountSynced,
    selectedDate,
    selectedGroup.key,
    selectedFilters,
  ]);

  // Replace the old useEffect with this new one that handles multiple cards
  useEffect(() => {
    // Function to update a specific table's height
    const updateTableHeight = (cardElement: Element) => {
      const cardId = cardElement.getAttribute("data-card-id");
      if (!cardId) return;

      const tableIndex = cardId.split("-")[1];
      const tableElement = document.querySelector(
        `[data-table-id="table-${tableIndex}"]`,
      );

      if (tableElement) {
        (tableElement as HTMLElement).style.height =
          `${(cardElement as HTMLElement).offsetHeight}px`;
      }
    };

    // Create ResizeObserver
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        updateTableHeight(entry.target);
      });
    });

    // Observe all cards
    document.querySelectorAll("[data-card-id]").forEach((card) => {
      resizeObserver.observe(card);
      // Initial height set
      updateTableHeight(card);
    });

    // Cleanup observer when component unmounts
    return () => resizeObserver.disconnect();
  }, [adList?.data?.rows, visibleAds]); // Add dependencies to re-run when rows or visible ads change

  // Add scroll based loading logic
  useEffect(() => {
    if (!adList?.data) return;
    const options = {
      root: null,
      rootMargin: "100px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && adList?.data?.rows) {
        setVisibleAds((prev) => Math.min(prev + 3, adList.data?.rows.length ?? 0));
      }
    }, options);

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current);
    }

    return () => observer.disconnect();
  }, [adList?.data]);

  // Reset visible ads when data changes
  useEffect(() => {
    setVisibleAds(3);
  }, [selectedDate, selectedFilters, adList?.data?.rows]);

  if (isLoadingConfigurations)
    return (
      <ExpiredTokenGuard>
        <div className="w-full h-full flex justify-center items-center">
          <PageLoader />
        </div>
      </ExpiredTokenGuard>
    );

  return (
    <ExpiredTokenGuard>
      <div className="flex flex-col w-full h-full pt-[8px] bg-background-gray">
        {/*Button Section*/}
        <div className="w-full flex justify-between pl-[9px] pr-[14px] mb-[25px]">
          <div className="flex gap-2">
            {sidebarCollapsed && <div className="h-[32px] w-[32px] shrink-0"></div>}
            {selectedAccount?.time_zone && (
              <CreativeReportDateSelector
                selectedFilters={selectedDate}
                setSelectedFilters={(metadata) => {
                  if (metadata) {
                    setSelectedDate(metadata);
                  }
                }}
                first_allowed_date={allowedDateRange?.first_allowed_date}
                last_allowed_date={allowedDateRange?.last_allowed_date}
                timezone={selectedAccount.time_zone}
              />
            )}
          </div>
        </div>
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Report Title */}
          <div className="w-full flex justify-between pl-[9px] pr-[14px]">
            <ReportTitle
              title={selectedSidebarItem?.label ?? ""}
              groupName={selectedGroup.label}
              icon={
                selectedSidebarItem && (
                  <selectedSidebarItem.icon size={16} className="text-brand-orange" />
                )
              }
              iconBackgroundColor="#FF45000F"
            />
          </div>
          {/* Filters */}
          <div className="mt-[12px] flex flex-wrap items-center gap-2 pl-[9px] pr-[14px]">
            {dataConfigurations && (
              <CreativeReportDropdowns
                dropdowns={
                  dataConfigurations?.dropdowns?.filter(
                    (d) => d?.key === "creative_type",
                  ) ?? []
                }
                dropdownsValues={selectedFilters?.dropdowns ?? {}}
                onChange={(dropdownValues) =>
                  setSelectedFilters({ ...selectedFilters, dropdowns: dropdownValues })
                }
              />
            )}
            {dataConfigurations?.filter && (
              <Filters
                selectedFilters={selectedFilters?.filters ?? []}
                onFilterChange={(filters) =>
                  filters && setSelectedFilters({ ...selectedFilters, filters })
                }
                selectedDate={selectedDate?.date ?? null}
                dataConfigFilter={dataConfigurations?.filter || {}}
                showAddFilters={true}
                customTrigger={
                  <SqButton
                    variant="outline"
                    className="border-border-gray h-8"
                    shadow={"sm"}
                  >
                    <div className="flex items-center gap-2">
                      <IconFilter className="h-4 w-4 text-brand-orange" />
                      <span className="text-brand-orange text-[12px] font-normal mt-[2px]">
                        Add Filter
                      </span>
                    </div>
                  </SqButton>
                }
                selectedFilterSize={32}
                className="contents"
              />
            )}
          </div>
          <Separator className="w-full mt-[19px]" color="#67749B29" />
          <div
            ref={scrollContainerRef}
            className={`w-full flex-1 ${
              selectedGroup.key === "overall" ? "overflow-hidden" : "overflow-y-auto"
            }`}
          >
            {selectedGroup.key === "overall" && (
              <div className="w-full h-full px-[9px] py-[26px]">
                <CommentAnalysisTable
                  accountId={selectedAccount?.id}
                  selectedFilters={selectedFilters}
                  selectedDate={selectedDate}
                />
              </div>
            )}
            {selectedGroup.key === "assets" && (
              <>
                {isLoadingAdList ? (
                  <div className="w-full h-full flex justify-center items-center">
                    <PageLoader message={adList?.message} />
                  </div>
                ) : adListError || adList?.status === "failed" ? (
                  <div className="w-full h-full flex justify-center items-center">
                    <div className="my-[250px] w-full flex flex-col justify-center items-center">
                      <div className="text-destructive text-lg font-medium mb-2">
                        Error loading data
                      </div>
                      <div className="text-muted-foreground text-sm mb-4">
                        Failed to fetch data for this section. Please try again.
                      </div>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setAdList(null);
                          setAdListError(null);
                          setIsLoadingAdList(false);
                          // Re-trigger the fetch by updating a dependency
                          setSelectedDate({ ...selectedDate! });
                        }}
                      >
                        Retry
                      </Button>
                    </div>
                  </div>
                ) : adList?.data?.rows.length === 0 ? (
                  <div className="w-full h-full">
                    <NoCommentsMessage />
                  </div>
                ) : (
                  <>
                    {adList?.data?.rows.slice(0, visibleAds).map((row, index) => (
                      <div key={index} className="w-full flex flex-col">
                        <div className="w-full flex gap-[9px] px-[9px] py-[26px]">
                          <div
                            className="w-[296px] h-min flex flex-col justify-between gap-[11px]"
                            data-card-id={`card-${index}`}
                          >
                            {row.creative && (
                              <AdCard
                                header={{
                                  title: row.creative?.account_name || "",
                                  icon: row.creative?.account_icon_url,
                                }}
                                body={{
                                  icon_src: getPlatformIcon(
                                    row.creative?.platform ?? Platform.Facebook,
                                  ),
                                  name: row.ad_name as string,
                                  description: row.creative?.bodies?.[0] || "",
                                  media: {
                                    ad_id: row.ad_id as string,
                                    source: row.creative?.videos?.[0]
                                      ? row.creative?.videos?.[0]?.permalink_url
                                      : row.creative?.images?.[0]?.permalink_url,
                                    type: row.creative?.videos?.[0] ? "video" : "image",
                                    thumbnail: row.creative.thumbnail_url,
                                    actions: [
                                      {
                                        component: (
                                          <DownloadAction
                                            url={
                                              row.creative_type === "video"
                                                ? row.creative?.videos?.[0]?.permalink_url
                                                : row.creative?.images?.[0]
                                                    ?.uncompressed_url ||
                                                  row.creative?.images?.[0]?.permalink_url
                                            }
                                            type={row.creative_type}
                                            accountName={row.creative?.account_name || ""}
                                          />
                                        ),
                                        position: "top-right",
                                        visibility: "hover",
                                      },
                                    ],
                                  },
                                }}
                                footer={{
                                  cta_label:
                                    row.creative?.call_to_action_titles?.[0] || "",
                                  landing_page: row.creative?.landing_pages?.[0] || "",
                                  title: row.creative?.titles?.[0] || "",
                                }}
                              />
                            )}
                            {row.creative?.platform !== Platform.Tiktok && (
                              <div className="flex w-full items-center justify-between gap-[12px]">
                                <div
                                  className="relative flex flex-1 h-[36px] items-center justify-center py-[7.2px] rounded-[8px] border border-border-gray shadow-sm flex-shrink-0 cursor-pointer bg-white hover:bg-hover-gray"
                                  onClick={() => {
                                    if (
                                      row.creative?.fb_post_url &&
                                      row.creative?.fb_post_url !== "N/A"
                                    ) {
                                      window.open(row.creative?.fb_post_url, "_blank");
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-[7px]">
                                    <img
                                      src={PLATFORM_CONFIG.facebook.icon}
                                      alt={PLATFORM_CONFIG.facebook.name}
                                      className="w-[15px] h-[15px] object-contain"
                                    />
                                    <span className="text-[12.6px] text-[rgba(2,24,89,0.8)] font-[Inter] font-normal leading-[18px]">
                                      {PLATFORM_CONFIG.facebook.name}
                                    </span>
                                    <div className="flex justify-center items-center border-none">
                                      <RxOpenInNewWindow
                                        size={16}
                                        className="shrink-0 text-brand-primary-800"
                                      />
                                    </div>
                                  </div>
                                  {(!row.creative?.fb_post_url ||
                                    row.creative?.fb_post_url === "N/A") && (
                                    <div className="absolute w-full h-full z-10 bg-white/50 cursor-not-allowed"></div>
                                  )}
                                </div>
                                <div
                                  className="relative flex flex-1 h-[36px] items-center justify-center py-[7.2px] rounded-[8px] border border-border-gray shadow-sm flex-shrink-0 cursor-pointer bg-white hover:bg-hover-gray"
                                  onClick={() => {
                                    if (
                                      row.creative?.ig_post_url &&
                                      row.creative?.ig_post_url !== "N/A"
                                    ) {
                                      window.open(row.creative?.ig_post_url, "_blank");
                                    }
                                  }}
                                >
                                  <div className="flex items-center gap-[7px]">
                                    <img
                                      src={PLATFORM_CONFIG.instagram.icon}
                                      alt={PLATFORM_CONFIG.instagram.name}
                                      className="w-[15px] h-[15px] object-contain"
                                    />
                                    <span className="text-[12.6px] text-[rgba(2,24,89,0.8)] font-[Inter] font-normal leading-[18px]">
                                      {PLATFORM_CONFIG.instagram.name}
                                    </span>
                                    <div className="flex justify-center items-center border-none">
                                      <RxOpenInNewWindow
                                        size={16}
                                        className="shrink-0 text-brand-primary-800"
                                      />
                                    </div>
                                  </div>
                                  {(!row.creative?.ig_post_url ||
                                    row.creative?.ig_post_url === "N/A") && (
                                    <div className="absolute w-full h-full z-10 bg-white/50 cursor-not-allowed"></div>
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="flex flex-col gap-[12px] w-full items-center p-[12px_14px] rounded-[8px] border border-border-gray flex-shrink-0 bg-white">
                              {adList?.data?.columns.map((column, colIndex) => {
                                if (column.column_type === "number") {
                                  const value = row[column.key] as number;
                                  return (
                                    <div
                                      key={colIndex}
                                      className="w-full flex justify-between"
                                    >
                                      <div className="text-[12px] text-brand-primary-800 font-normal leading-[19.157px] tracking-[0.24px] truncate">
                                        {column.label}
                                      </div>
                                      <div className="text-[12px] text-brand-primary-800 font-normal leading-[19.157px] tracking-[0.24px] truncate">
                                        {column.prefix +
                                          " " +
                                          value +
                                          " " +
                                          column.suffix}
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                          <div
                            className="flex-1 overflow-hidden"
                            data-table-id={`table-${index}`}
                          >
                            <CommentAnalysisTable
                              accountId={selectedAccount?.id}
                              selectedFilters={adSelectedFiltersRef.current}
                              selectedDate={adSelectedDateRef.current}
                              ad_id={row.ad_id as string}
                              maxHeight="100%"
                              isLoadingAdList={isLoadingAdList}
                            />
                          </div>
                        </div>
                        <Separator
                          className="w-full mt-[20px] mb-[12px]"
                          color="#67749B29"
                        />
                      </div>
                    ))}

                    {/* Load more trigger */}
                    {adList?.data?.rows && visibleAds < adList.data.rows.length && (
                      <div
                        ref={loadMoreTriggerRef}
                        className="w-full h-20 flex justify-center items-center"
                      ></div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </ExpiredTokenGuard>
  );
};

export default CommentAnalysisReport;
